const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/
const LOCATION_SDP_REGEX = /:sdp:[^:]+$/i
const MONTHS_IN_RESPONSE = 12
const TREND_LOOKBACK_MONTHS = 3

function checkForRequirements (pgConnection, location) {
  if (!pgConnection) {
    const err = new Error('Postgres connection is required')
    err.status = 500
    throw err
  }

  if (!location) {
    const err = new Error('location is required')
    err.status = 400
    throw err
  }
}

function parseDateOnlyUtcOrThrow (value, fieldName) {
  if (!DATE_ONLY_REGEX.test(value)) {
    throw new Error(`${fieldName} must be in YYYY-MM-DD format`)
  }

  const date = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  if (date.toISOString().slice(0, 10) !== value) {
    throw new Error(`${fieldName} is not a valid date`)
  }

  return date
}

function resolveLocationScope (location) {
  if (location === 'national') {
    return {
      location: 'national',
      scopeType: 'national'
    }
  }

  const scopeType = LOCATION_SDP_REGEX.test(location) ? 'sdp' : 'regional'
  return {
    location: location,
    scopeType: scopeType
  }
}

function buildAnalyticsBaseContext ({ fromDate, toDate, location }) {
  const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
  const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
  const toDateExclusiveUtc = new Date(toDateUtc)
  toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)

  return {
    fromDateUtc: fromDateUtc,
    toDateUtc: toDateUtc,
    toDateExclusiveUtc: toDateExclusiveUtc,
    locationScope: resolveLocationScope(location)
  }
}

function buildRevenueQueryContext ({ organisationId, fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope, productSku, serviceIds }) {
  const currentMonthStartUtc = new Date(Date.UTC(fromDateUtc.getUTCFullYear(), fromDateUtc.getUTCMonth(), 1))
  const previous3MonthsStartUtc = new Date(Date.UTC(currentMonthStartUtc.getUTCFullYear(), currentMonthStartUtc.getUTCMonth() - 3, 1))
  // The trend delta always compares the current month against the prior 3 months, so its
  // "current" side is normalised over month-to-date — independent of the selected reporting
  // period, which may be a single day for daily tenants (RMS) or a full month (EPN).
  const currentMonthDays = calculateInclusiveDays(currentMonthStartUtc, toDateUtc)
  const previous3MonthsPeriodDays = calculateInclusiveDays(previous3MonthsStartUtc, new Date(currentMonthStartUtc.getTime() - (24 * 60 * 60 * 1000)))
  const params = [
    organisationId, // $1
    previous3MonthsStartUtc.toISOString(), // $2 trend: prior-3-months start
    toDateExclusiveUtc.toISOString(), // $3 outer filter end (toDate + 1 day)
    currentMonthStartUtc.toISOString(), // $4 trend: current-month start
    toDateExclusiveUtc.toISOString(), // $5 window end (toDate + 1 day)
    fromDateUtc.toISOString() // $6 selected reporting-period start
  ]
  const whereClauses = []

  appendScopeAndProductFilters({ params, whereClauses, locationScope, productSku, serviceIds })

  return {
    currentMonthDays: currentMonthDays,
    previous3MonthsPeriodDays: previous3MonthsPeriodDays,
    params: params,
    whereClauses: whereClauses
  }
}

function buildMonthlyRevenueDateWindow ({ toDate, location }) {
  const selectedToDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
  const toMonthStartUtc = new Date(Date.UTC(selectedToDateUtc.getUTCFullYear(), selectedToDateUtc.getUTCMonth(), 1))
  const toDateExclusiveUtc = new Date(Date.UTC(toMonthStartUtc.getUTCFullYear(), toMonthStartUtc.getUTCMonth() + 1, 1))
  const toDateUtc = new Date(Date.UTC(toMonthStartUtc.getUTCFullYear(), toMonthStartUtc.getUTCMonth() + 1, 0))
  const fromDateUtc = new Date(Date.UTC(toMonthStartUtc.getUTCFullYear(), toMonthStartUtc.getUTCMonth() - (MONTHS_IN_RESPONSE - 1), 1))

  return {
    fromDate: fromDateUtc.toISOString().slice(0, 10),
    fromDateUtc: fromDateUtc,
    toDateUtc: toDateUtc,
    toDateExclusiveUtc: toDateExclusiveUtc,
    locationScope: resolveLocationScope(location)
  }
}

function buildMonthlyRevenueQueryContext ({ organisationId, fromDateUtc, toDateExclusiveUtc, locationScope, productSku, serviceIds }) {
  const trendWindowStartUtc = new Date(Date.UTC(fromDateUtc.getUTCFullYear(), fromDateUtc.getUTCMonth() - TREND_LOOKBACK_MONTHS, 1))
  const params = [
    organisationId,
    trendWindowStartUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]
  const whereClauses = []

  appendScopeAndProductFilters({ params, whereClauses, locationScope, productSku, serviceIds })

  return {
    params: params,
    whereClauses: whereClauses
  }
}

function appendScopeAndProductFilters ({ params, whereClauses, locationScope, productSku, serviceIds }) {
  if (locationScope.scopeType !== 'national') {
    const exactIndex = params.length + 1
    const descendantIndex = params.length + 2
    whereClauses.push(`
        AND (
          dl.fsid = $${exactIndex}
          OR dl.fsid LIKE $${descendantIndex}
        )
    `)
    params.push(locationScope.location, `${locationScope.location}:%`)
  }

  if (productSku) {
    whereClauses.push(`AND si.product_sku_id = $${params.length + 1}`)
    params.push(productSku)
  }

  if (serviceIds && serviceIds.length > 0) {
    whereClauses.push(`AND si.service_id = ANY($${params.length + 1}::uuid[])`)
    params.push(serviceIds)
  }
}

// if aggregationLevel is 'warehouse' (RMS), always group by individual warehouse (:name: level),
// regardless of whether location is national, a zone, a state, etc. - the WHERE clause
// (appendScopeAndProductFilters) already scopes rows down to that location, so grouping just
// needs to bucket whatever comes back by warehouse.
// otherwise (EPN-style district hierarchy):
// if location is national, then
// - if the location starts with zone:, then group by zone
// - if the location starts with country:, then group by country + next key:value (eg. state)
// - fallback is to group by state
// if the location is zone (zone:* or country:*:zone:*) then group by state
// if the location is state then group by lga
// if the location is lga then group by sdp
function getLocationTableGrouping (location, aggregationLevel) {
  if (aggregationLevel === 'warehouse') {
    return {
      groupByLevel: 'name',
      scopeExpression: `substring(dl.fsid from '^(.*:name:[^:]+)')`
    }
  }

  if (location === 'national') {
    return {
      groupByLevel: 'state',
      scopeExpression: `
        CASE
          WHEN dl.fsid ~* '^zone:[^:]+' THEN substring(dl.fsid from '^(zone:[^:]+)')
          WHEN dl.fsid ~* '^country:[^:]+:[^:]+:[^:]+' THEN substring(dl.fsid from '^(country:[^:]+:[^:]+:[^:]+)')
          ELSE substring(dl.fsid from '^(.*:state:[^:]+)')
        END
      `
    }
  }

  if (/^(?:country:[^:]+:)?zone:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'state',
      scopeExpression: `substring(dl.fsid from '^(.*:state:[^:]+)')`
    }
  }

  if (/:state:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'lga',
      scopeExpression: `substring(dl.fsid from '^(.*:lga:[^:]+)')`
    }
  }

  if (/:lga:[^:]+$/i.test(location)) {
    return {
      groupByLevel: 'sdp',
      scopeExpression: `substring(dl.fsid from '^(.*:sdp:[^:]+)')`
    }
  }

  const err = new Error('location must be national, state, zone or lga scope for revenue location table')
  err.status = 400
  throw err
}

function calculateInclusiveDays (fromDateUtc, toDateUtc) {
  const msPerDay = 24 * 60 * 60 * 1000
  return Math.floor((toDateUtc.getTime() - fromDateUtc.getTime()) / msPerDay) + 1
}

function getNationalGroupKeyFromLocationFsid (locationFsid) {
  const fsid = String(locationFsid || '')
  const zoneMatch = fsid.match(/^(zone:[^:]+)/i)
  if (zoneMatch) {
    return zoneMatch[1]
  }

  const countryMatch = fsid.match(/^(country:[^:]+:[^:]+:[^:]+)/i)
  if (countryMatch) {
    return countryMatch[1]
  }

  return fsid
}

// this is used to aggregate the rows by the national level
// different countries have different levels of aggregation -
// in Nigeria, the highest level of aggregation is the zone level while in Kenya it's the state
// when aggregationLevel is 'warehouse' (RMS), rows are already grouped at warehouse level by the SQL so no further aggregation is needed
function aggregateNationalLocationRows (rows, aggregationLevel) {
  if (aggregationLevel === 'warehouse') {
    return rows
  }

  const grouped = rows.reduce((acc, row) => {
    const key = getNationalGroupKeyFromLocationFsid(row.location_fsid)
    const current = acc[key] || {
      location_fsid: key,
      revenue_total: 0,
      current_month_revenue_total: 0,
      previous_3_months_revenue_total: 0,
      percent_of_total: 0
    }

    current.revenue_total += Number(row.revenue_total || 0)
    current.current_month_revenue_total += Number(row.current_month_revenue_total || 0)
    current.previous_3_months_revenue_total += Number(row.previous_3_months_revenue_total || 0)
    current.percent_of_total += Number(row.percent_of_total || 0)
    acc[key] = current
    return acc
  }, {})

  return Object.values(grouped).sort((a, b) => {
    const revenueDiff = Number(b.revenue_total || 0) - Number(a.revenue_total || 0)
    if (revenueDiff !== 0) {
      return revenueDiff
    }
    return String(a.location_fsid).localeCompare(String(b.location_fsid))
  })
}

function calculateDeltaPct (currentValue, baselineValue) {
  if (baselineValue === 0) {
    return null
  }
  return roundTo2(((currentValue - baselineValue) / baselineValue) * 100)
}

function roundTo2 (value) {
  return Math.round(value * 100) / 100
}

function getCurrentUtcMonthKey () {
  const now = new Date()
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

function mapMonthlyRevenueRows (queryRows, currentMonthKey) {
  const monthlyRows = queryRows.map((row) => ({
    month: row.month,
    revenueTotal: Number(row.revenue_total || 0)
  }))

  return monthlyRows.map((row, index) => ({
    month: row.month,
    revenueTotal: row.revenueTotal,
    trend: calculateMonthlyRevenueTrend(monthlyRows, index, currentMonthKey)
  }))
}

function calculateMonthlyRevenueTrend (monthlyRows, rowIndex, currentMonthKey) {
  if (monthlyRows[rowIndex].month === currentMonthKey) {
    return null
  }
  if (rowIndex < TREND_LOOKBACK_MONTHS) {
    return null
  }
  const previous3MonthsRevenue = monthlyRows.slice(rowIndex - TREND_LOOKBACK_MONTHS, rowIndex)
    .reduce((sum, row) => sum + row.revenueTotal, 0)
  return roundTo2(previous3MonthsRevenue / TREND_LOOKBACK_MONTHS)
}

async function getRevenueSummary ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildRevenueQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })

  const query = `
      WITH filtered AS (
        SELECT
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(*) FILTER (WHERE time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS total_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NOT NULL AND time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS priced_rows,
        COUNT(*) FILTER (WHERE unit_price_at_sale IS NULL AND time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz)::int AS missing_price_rows,
        COALESCE(SUM(units_sold) FILTER (WHERE time_of_sale >= $6::timestamptz AND time_of_sale < $5::timestamptz), 0)::numeric AS units_sold_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE unit_price_at_sale IS NOT NULL
              AND time_of_sale >= $6::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS revenue_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE unit_price_at_sale IS NOT NULL
              AND time_of_sale >= $4::timestamptz
              AND time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS current_month_revenue_total,
        COALESCE(
          SUM(units_sold * unit_price_at_sale) FILTER (
            WHERE unit_price_at_sale IS NOT NULL
              AND time_of_sale >= $2::timestamptz
              AND time_of_sale < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_revenue_total
      FROM filtered
    `

  const { rows } = await pgConnection.query(query, params)
  const row = rows[0] || {}
  const revenueTotal = Number(row.revenue_total || 0)
  const currentMonthRevenueTotal = Number(row.current_month_revenue_total || 0)
  const previous3MonthsRevenueTotal = Number(row.previous_3_months_revenue_total || 0)
  const currentAverageDailyRevenue = currentMonthRevenueTotal / currentMonthDays
  const previous3MonthsAverageDailyRevenue = previous3MonthsRevenueTotal / previous3MonthsPeriodDays
  const revenueDeltaPercent = calculateDeltaPct(currentAverageDailyRevenue, previous3MonthsAverageDailyRevenue)

  return {
    fromDate,
    toDate,
    organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    totalRows: Number(row.total_rows || 0),
    pricedRows: Number(row.priced_rows || 0),
    missingPriceRows: Number(row.missing_price_rows || 0),
    unitsSoldTotal: Number(row.units_sold_total || 0),
    revenueTotal: revenueTotal,
    previous3MonthsAverageDailyRevenue: roundTo2(previous3MonthsAverageDailyRevenue),
    currentAverageDailyRevenue: roundTo2(currentAverageDailyRevenue),
    revenueDeltaPercent: revenueDeltaPercent
  }
}

async function getRevenueByLocationTable ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds, aggregationLevel }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildRevenueQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })
  const grouping = getLocationTableGrouping(locationScope.location, aggregationLevel)

  const query = `
      WITH filtered AS (
        SELECT
          ${grouping.scopeExpression} AS location_fsid,
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        location_fsid,
        revenue_total,
        current_month_revenue_total,
        previous_3_months_revenue_total,
        CASE
          WHEN SUM(revenue_total) OVER () = 0::numeric THEN 0::numeric
          ELSE (revenue_total / SUM(revenue_total) OVER ()) * 100
        END AS percent_of_total
      FROM (
        SELECT
          location_fsid,
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE unit_price_at_sale IS NOT NULL
                AND time_of_sale >= $6::timestamptz
                AND time_of_sale < $5::timestamptz
            ),
            0
          )::numeric AS revenue_total,
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE unit_price_at_sale IS NOT NULL
                AND time_of_sale >= $4::timestamptz
                AND time_of_sale < $5::timestamptz
            ),
            0
          )::numeric AS current_month_revenue_total,
          COALESCE(
            SUM(units_sold * unit_price_at_sale) FILTER (
              WHERE unit_price_at_sale IS NOT NULL
                AND time_of_sale >= $2::timestamptz
                AND time_of_sale < $4::timestamptz
            ),
            0
          )::numeric AS previous_3_months_revenue_total
        FROM filtered
        WHERE location_fsid IS NOT NULL
        GROUP BY location_fsid
      ) grouped
      ORDER BY revenue_total DESC, location_fsid ASC
    `

  const { rows } = await pgConnection.query(query, params)
  const groupedRows = locationScope.location === 'national'
    ? aggregateNationalLocationRows(rows, aggregationLevel)
    : rows
  return {
    fromDate: fromDate,
    toDate: toDate,
    organisationId: organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: groupedRows.map((row) => ({
      locationFsid: row.location_fsid,
      revenueTotal: Number(row.revenue_total || 0),
      percentOfTotal: Number(row.percent_of_total || 0),
      previous3MonthsAverageDailyRevenue: roundTo2(Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays),
      currentAverageDailyRevenue: roundTo2(Number(row.current_month_revenue_total || 0) / currentMonthDays),
      revenueDeltaPercent: calculateDeltaPct(
        Number(row.current_month_revenue_total || 0) / currentMonthDays,
        Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays
      )
    }))
  }
}

async function getRevenueByProductTable ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const { currentMonthDays, previous3MonthsPeriodDays, params, whereClauses } = buildRevenueQueryContext({
    organisationId,
    fromDateUtc,
    toDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })

  const query = `
      WITH filtered AS (
        SELECT
          si.product_sku_id,
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        f.product_sku_id AS product_sku,
        COALESCE(ps.display_name, f.product_sku_id) AS product_name,
        COALESCE(
          SUM(f.units_sold) FILTER (
            WHERE f.units_sold IS NOT NULL
              AND f.time_of_sale >= $6::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_total,
        COALESCE(
          SUM(f.units_sold) FILTER (
            WHERE f.unit_price_at_sale IS NOT NULL
              AND f.time_of_sale >= $6::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_with_price,
        COALESCE(
          SUM(f.units_sold) FILTER (
            WHERE f.unit_price_at_sale IS NULL
              AND f.time_of_sale >= $6::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS units_sold_without_price,
        COALESCE(
          SUM(f.units_sold * f.unit_price_at_sale) FILTER (
            WHERE f.unit_price_at_sale IS NOT NULL
              AND f.time_of_sale >= $6::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS revenue_total,
        COALESCE(
          SUM(f.units_sold * f.unit_price_at_sale) FILTER (
            WHERE f.unit_price_at_sale IS NOT NULL
              AND f.time_of_sale >= $4::timestamptz
              AND f.time_of_sale < $5::timestamptz
          ),
          0
        )::numeric AS current_month_revenue_total,
        COALESCE(
          SUM(f.units_sold * f.unit_price_at_sale) FILTER (
            WHERE f.unit_price_at_sale IS NOT NULL
              AND f.time_of_sale >= $2::timestamptz
              AND f.time_of_sale < $4::timestamptz
          ),
          0
        )::numeric AS previous_3_months_revenue_total
      FROM filtered f
      LEFT JOIN avocado.data_productsku ps
        ON ps.old_sku_id = f.product_sku_id
      WHERE f.product_sku_id IS NOT NULL
      GROUP BY f.product_sku_id, ps.display_name
      HAVING COALESCE(
        SUM(f.units_sold) FILTER (
          WHERE f.units_sold IS NOT NULL
            AND f.time_of_sale >= $6::timestamptz
            AND f.time_of_sale < $5::timestamptz
        ),
        0
      )::numeric > 0::numeric
      ORDER BY revenue_total DESC, product_sku ASC
    `

  const { rows } = await pgConnection.query(query, params)
  return {
    fromDate: fromDate,
    toDate: toDate,
    organisationId: organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: rows.map((row) => ({
      productSku: row.product_sku,
      productName: row.product_name,
      unitsSoldTotal: Number(row.units_sold_total || 0),
      unitsSoldWithPrice: Number(row.units_sold_with_price || 0),
      unitsSoldWithoutPrice: Number(row.units_sold_without_price || 0),
      revenueTotal: Number(row.revenue_total || 0),
      previous3MonthsAverageDailyRevenue: roundTo2(Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays),
      currentAverageDailyRevenue: roundTo2(Number(row.current_month_revenue_total || 0) / currentMonthDays),
      revenueDeltaPercent: calculateDeltaPct(
        Number(row.current_month_revenue_total || 0) / currentMonthDays,
        Number(row.previous_3_months_revenue_total || 0) / previous3MonthsPeriodDays
      )
    }))
  }
}

async function getRevenueMissingPricesSummary ({ pgConnection, fromDate, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDateUtc, toDateExclusiveUtc, locationScope } = buildAnalyticsBaseContext({ fromDate, toDate, location })
  const params = [
    organisationId,
    fromDateUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]
  const whereClauses = []
  appendScopeAndProductFilters({
    params,
    whereClauses,
    locationScope,
    productSku,
    serviceIds
  })

  const summaryQuery = `
      WITH filtered AS (
        SELECT
          si.location_id,
          si.product_sku_id,
          si.units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        COUNT(DISTINCT product_sku_id) FILTER (
          WHERE product_sku_id IS NOT NULL
            AND COALESCE(units_sold, 0) > 0
        )::int AS total_sku_count,
        COUNT(DISTINCT product_sku_id) FILTER (
          WHERE product_sku_id IS NOT NULL
            AND COALESCE(units_sold, 0) > 0
            AND unit_price_at_sale IS NULL
        )::int AS missing_sku_count,
        COUNT(DISTINCT location_id) FILTER (
          WHERE location_id IS NOT NULL
            AND product_sku_id IS NOT NULL
            AND COALESCE(units_sold, 0) > 0
            AND unit_price_at_sale IS NULL
        )::int AS facilities_with_missing_prices_count
      FROM filtered
  `

  const facilitiesQuery = `
      WITH missing_sku_per_facility AS (
        SELECT DISTINCT
          si.location_id,
          si.product_sku_id
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          AND si.unit_price_at_sale IS NULL
          AND COALESCE(si.units_sold, 0) > 0
          AND si.product_sku_id IS NOT NULL
          ${whereClauses.join('\n')}
      ),
      expanded AS (
        SELECT
          dl.fsid AS location_fsid,
          dl.name_of_pharmacy AS location_name,
          dl.email AS contact_email,
          COALESCE(dl.owners_phone_number, dl.alt_phone_number) AS contact_number,
          m.product_sku_id AS product_sku,
          COALESCE(ps.display_name, m.product_sku_id) AS product_name
        FROM missing_sku_per_facility m
        INNER JOIN avocado.data_location dl
          ON dl.uuid = m.location_id
        LEFT JOIN avocado.data_productsku ps
          ON ps.old_sku_id = m.product_sku_id
      )
      SELECT
        location_fsid,
        location_name,
        contact_email,
        contact_number,
        json_agg(
          json_build_object(
            'productSku', product_sku,
            'productName', product_name
          )
          ORDER BY product_name ASC, product_sku ASC
        ) AS missing_products
      FROM expanded
      GROUP BY location_fsid, location_name, contact_email, contact_number
      ORDER BY location_name ASC NULLS LAST, location_fsid ASC
  `

  const [{ rows: [summary = {}] }, { rows: facilitiesRows }] = await Promise.all([
    pgConnection.query(summaryQuery, params),
    pgConnection.query(facilitiesQuery, params)
  ])

  const totalSkuCount = Number(summary.total_sku_count || 0)
  const missingSkuCount = Number(summary.missing_sku_count || 0)
  const facilitiesWithMissingPricesCount = Number(summary.facilities_with_missing_prices_count || 0)

  return {
    fromDate: fromDate,
    toDate: toDate,
    organisationId: organisationId,
    location: locationScope.location,
    missingSkuCount: missingSkuCount,
    totalSkuCount: totalSkuCount,
    missingSkuPercent: totalSkuCount > 0 ? (missingSkuCount / totalSkuCount) : null,
    facilitiesWithMissingPricesCount: facilitiesWithMissingPricesCount,
    facilities: facilitiesRows.map((row) => ({
      locationFsid: row.location_fsid,
      locationName: row.location_name,
      contactEmail: row.contact_email,
      contactNumber: row.contact_number,
      missingProducts: Array.isArray(row.missing_products) ? row.missing_products : []
    }))
  }
}

async function getMonthlyRevenue ({ pgConnection, toDate, organisationId, location, productSku, serviceIds }) {
  checkForRequirements(pgConnection, location)
  const { fromDate, fromDateUtc, toDateUtc, toDateExclusiveUtc, locationScope } = buildMonthlyRevenueDateWindow({ toDate, location })
  const { params, whereClauses } = buildMonthlyRevenueQueryContext({
    organisationId,
    fromDateUtc,
    toDateExclusiveUtc,
    locationScope,
    productSku,
    serviceIds
  })

  const query = `
      WITH months AS (
        SELECT generate_series(
          date_trunc('month', $2::timestamptz),
          date_trunc('month', $3::timestamptz - interval '1 day'),
          interval '1 month'
        ) AS month_start
      ),
      filtered AS (
        SELECT
          si.time_of_sale,
          si.units_sold::numeric AS units_sold,
          si.unit_price_at_sale
        FROM analytics.sales_indicators si
        INNER JOIN avocado.data_location dl
          ON dl.uuid = si.location_id
        WHERE organisation_id = $1
          AND si.time_of_sale >= $2::timestamptz
          AND si.time_of_sale < $3::timestamptz
          ${whereClauses.join('\n')}
      )
      SELECT
        to_char(months.month_start, 'YYYY-MM') AS month,
        COALESCE(SUM(filtered.units_sold * filtered.unit_price_at_sale), 0)::numeric AS revenue_total
      FROM months
      LEFT JOIN filtered
        ON filtered.time_of_sale >= months.month_start
        AND filtered.time_of_sale < months.month_start + interval '1 month'
        AND filtered.unit_price_at_sale IS NOT NULL
      GROUP BY months.month_start
      ORDER BY months.month_start ASC
    `

  const { rows } = await pgConnection.query(query, params)
  const currentMonthKey = getCurrentUtcMonthKey()
  const rowsWithTrend = mapMonthlyRevenueRows(rows, currentMonthKey)

  return {
    fromDate: fromDate,
    toDate: toDateUtc.toISOString().slice(0, 10),
    organisationId: organisationId,
    location: locationScope.location,
    productSku: productSku || null,
    rows: rowsWithTrend.slice(-MONTHS_IN_RESPONSE)
  }
}

module.exports = {
  getRevenueSummary,
  getRevenueByLocationTable,
  getRevenueByProductTable,
  getRevenueMissingPricesSummary,
  getMonthlyRevenue,
  parseDateOnlyUtcOrThrow
}
